import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Search } from "lucide-react";
import { api, imageUrl, formatPrice } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

// Lightweight thumbnail with skeleton + variant fallback (thumbnail -> medium -> original).
// Used only in the Admin Products list to avoid loading full-size originals.
const THUMB_FALLBACK_CHAIN = ["thumbnail", "medium", null];
function AdminProductThumb({ src }) {
    const [stage, setStage] = useState(0);
    const [loaded, setLoaded] = useState(false);
    if (!src) return null;
    const variant = THUMB_FALLBACK_CHAIN[stage];
    const url = imageUrl(src, variant || undefined);
    return (
        <>
            {!loaded && (
                <div
                    className="absolute inset-0 bg-gradient-to-br from-[#1f1f23] to-[#101012] animate-pulse"
                    aria-hidden="true"
                />
            )}
            <img
                src={url}
                alt=""
                width={40}
                height={40}
                loading="lazy"
                decoding="async"
                onLoad={() => setLoaded(true)}
                onError={() => {
                    if (stage < THUMB_FALLBACK_CHAIN.length - 1) {
                        setStage((s) => s + 1);
                    } else {
                        setLoaded(true);
                    }
                }}
                className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
                data-testid="admin-product-thumb"
            />
        </>
    );
}

export default function AdminProducts() {
    const { settings } = useSettings();
    const cur = settings?.currency_symbol || "Rs.";
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/products", { params: q ? { q } : {} });
            setProducts(data || []);
        } catch (_) {
            setProducts([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    const onSearch = (e) => {
        e.preventDefault();
        load();
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete ${p.name}?`)) return;
        try {
            await api.delete(`/admin/products/${p.id}`);
            toast.success("Product deleted");
            setProducts((arr) => arr.filter((x) => x.id !== p.id));
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-products-page">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Catalog</div>
                    <h1 className="font-display text-3xl font-black uppercase tracking-tight">Products</h1>
                </div>
                <button
                    onClick={() => navigate("/admin/products/new")}
                    className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#33C9EA]"
                    data-testid="admin-products-add"
                >
                    <Plus size={16} /> New product
                </button>
            </div>

            <form onSubmit={onSearch} className="relative max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name…"
                    className="w-full bg-[#18181B] border border-[#27272A] rounded-sm pl-9 pr-3 py-2.5 text-sm focus:border-[#00BCE5] outline-none"
                    data-testid="admin-products-search"
                />
            </form>

            <div className="bg-[#18181B] border border-[#27272A] rounded-sm overflow-hidden">
                {loading ? (
                    <div className="p-6 text-white/55 text-sm">Loading…</div>
                ) : products.length === 0 ? (
                    <div className="p-8 text-center text-white/55">No products yet. Click "New product" to add one.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-[#121214] text-xs uppercase tracking-widest font-display text-white/55">
                            <tr>
                                <th className="px-3 py-2 text-left">Product</th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-left">Price</th>
                                <th className="px-3 py-2 text-left">Stock</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p) => {
                                const stock =
                                    p.product_type === "variant"
                                        ? (p.variants || []).reduce((s, v) => s + (v.active ? Number(v.stock || 0) : 0), 0)
                                        : Number(p.stock || 0);
                                const minPrice =
                                    p.product_type === "variant"
                                        ? Math.min(...(p.variants || []).filter((v) => v.active).map((v) => Number(v.sale_price || v.price)))
                                        : Number(p.sale_price || p.price);
                                return (
                                    <tr key={p.id} className="border-t border-[#27272A]" data-testid={`admin-product-row-${p.slug}`}>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-3">
                                                <div className="relative w-10 h-10 bg-[#0A0A0B] border border-[#27272A] rounded-sm overflow-hidden flex-shrink-0">
                                                    {p.primary_image && <AdminProductThumb src={p.primary_image} />}
                                                </div>
                                                <div className="min-w-0">
                                                    <Link to={`/admin/products/${p.id}`} className="font-medium text-white hover:text-[#00BCE5] line-clamp-1">
                                                        {p.name}
                                                    </Link>
                                                    <div className="text-[11px] text-white/45 truncate">{p.slug}</div>
                                                    {p.sku && !/^https?:\/\//i.test(p.sku) && (
                                                        <div className="text-[11px] text-white/35 truncate">SKU: {p.sku}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 uppercase font-display tracking-widest text-xs text-white/65">
                                            {p.product_type}
                                            {p.product_type === "variant" && (
                                                <span className="ml-2 text-[#00BCE5]">{(p.variants || []).length} v</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">{Number.isFinite(minPrice) ? formatPrice(minPrice, cur) : "—"}</td>
                                        <td className="px-3 py-2">
                                            <span className={stock <= 5 ? "text-[#FF9F0A] font-bold" : ""}>{stock}</span>
                                        </td>
                                        <td className="px-3 py-2 text-xs uppercase font-display tracking-widest">
                                            {p.active ? <span className="text-[#34C759]">Active</span> : <span className="text-white/45">Inactive</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="inline-flex gap-2">
                                                <Link
                                                    to={`/admin/products/${p.id}`}
                                                    className="p-1.5 border border-[#27272A] rounded-sm text-white/85 hover:border-[#00BCE5] hover:text-[#00BCE5]"
                                                    data-testid={`admin-product-edit-${p.slug}`}
                                                >
                                                    <Edit2 size={14} />
                                                </Link>
                                                <button
                                                    onClick={() => remove(p)}
                                                    className="p-1.5 border border-[#27272A] rounded-sm text-white/85 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                                                    data-testid={`admin-product-delete-${p.slug}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
